// Converts HTML that arrives from an external source (Dashpivot form fields
// such as Builder Comments) into plain text for rendering as a React text
// node. This is the only supported way to display such content: never pass
// external or user-typed markup to dangerouslySetInnerHTML, because any user
// with project access can write to claims_variations and the markup would
// execute as stored XSS for every other viewer.
//
// The converter keeps the visible structure (paragraph and line breaks become
// newlines, list items become "- " lines) and drops every tag and attribute.
// Pair it with a container that uses white-space: pre-wrap so the newlines
// survive.
//
// It is a single left-to-right scan with no backtracking regex over user
// input, and it caps the input length, so a hostile comment cannot freeze the
// tab of everyone who opens the variation. The longest real comment in
// production is under 200 characters.

export const MAX_HTML_TO_TEXT_LENGTH = 20000;

const BLOCK_TAGS = new Set([
  "p", "div", "br", "h1", "h2", "h3", "h4", "h5", "h6", "li", "ul", "ol",
  "tr", "table", "blockquote", "pre", "section", "article",
]);

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

// String.fromCodePoint throws a RangeError for values above 0x10FFFF, which
// would crash the modal on a hostile entity such as &#99999999;. Keep the
// original text for anything that is not a valid code point.
function fromCode(code: number, fallback: string): string {
  if (!Number.isInteger(code) || code < 0 || code > 0x10ffff) return fallback;
  return String.fromCodePoint(code);
}

function decodeEntities(input: string): string {
  return input.replace(
    /&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi,
    (match, body: string) => {
      const lower = body.toLowerCase();
      if (lower.startsWith("#x")) {
        return fromCode(parseInt(lower.slice(2), 16), match);
      }
      if (lower.startsWith("#")) {
        return fromCode(parseInt(lower.slice(1), 10), match);
      }
      return NAMED_ENTITIES[lower] ?? match;
    },
  );
}

function isLetter(c: string | undefined): boolean {
  return c !== undefined && ((c >= "a" && c <= "z") || (c >= "A" && c <= "Z"));
}

// "<" starts a tag only before a letter, "/", "!" or "?". Anything else
// ("5 < 6", "< 2m") is ordinary text.
function startsTag(c: string | undefined): boolean {
  return isLetter(c) || c === "/" || c === "!" || c === "?";
}

// ASCII-only lowercase keeps every index aligned with the original string.
function asciiLower(s: string): string {
  return s.replace(/[A-Z]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 32));
}

// Returns the index just past the ">" that ends the tag starting at `start`,
// honouring quoted attribute values (so <a href='>'> is one tag), or -1 when
// the tag never closes.
function findTagEnd(
  text: string,
  start: number,
  lastGt: number,
  lastQuote: { '"': number; "'": number },
): number {
  if (start > lastGt) return -1;
  let prev = "";
  let j = start + 1;
  while (j < text.length) {
    const c = text[j];
    if (c === ">") return j + 1;
    if ((c === '"' || c === "'") && prev === "=") {
      if (lastQuote[c] <= j) return -1;
      j = text.indexOf(c, j + 1) + 1;
      prev = c;
      if (j > lastGt) return -1;
      continue;
    }
    if (c !== " " && c !== "\t" && c !== "\n" && c !== "\r" && c !== "\f") {
      prev = c;
    }
    j++;
  }
  return -1;
}

export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return "";
  const text = String(html).slice(0, MAX_HTML_TO_TEXT_LENGTH);
  const lower = asciiLower(text);
  const lastGt = text.lastIndexOf(">");
  const lastQuote = {
    '"': text.lastIndexOf('"'),
    "'": text.lastIndexOf("'"),
  };

  const out: string[] = [];
  let atLineStart = true;
  const push = (s: string) => {
    if (!s) return;
    out.push(s);
    atLineStart = s.endsWith("\n");
  };
  const ensureNewline = () => {
    if (!atLineStart) push("\n");
  };

  // Once a search for a closing marker fails, no later search can succeed,
  // so remember it and never rescan (keeps the scan linear).
  const noCloser: Record<string, boolean> = {};
  let noCommentEnd = false;

  let i = 0;
  const n = text.length;
  while (i < n) {
    const lt = text.indexOf("<", i);
    if (lt === -1) {
      push(decodeEntities(text.slice(i)));
      break;
    }
    if (lt > i) push(decodeEntities(text.slice(i, lt)));

    if (!startsTag(text[lt + 1])) {
      push("<");
      i = lt + 1;
      continue;
    }

    // Comments: drop the whole comment, "-->" included.
    if (text.startsWith("<!--", lt)) {
      const end = noCommentEnd ? -1 : text.indexOf("-->", lt + 4);
      if (end !== -1) {
        i = end + 3;
        continue;
      }
      noCommentEnd = true;
    }

    const tagEnd = findTagEnd(text, lt, lastGt, lastQuote);
    if (tagEnd === -1) {
      // Never closes: it is not a tag, show it as typed (inert text).
      if (lt <= lastGt) {
        // A closing ">" exists further on but this start could not reach it
        // (an unterminated quoted value). Rescanning from every later "<"
        // would be quadratic, so show the rest as typed and stop.
        push(decodeEntities(text.slice(lt)));
        break;
      }
      push("<");
      i = lt + 1;
      continue;
    }

    let k = lt + 1;
    const closing = text[k] === "/";
    if (closing) k++;
    let nameEnd = k;
    while (
      nameEnd < tagEnd &&
      (isLetter(text[nameEnd]) || (text[nameEnd] >= "0" && text[nameEnd] <= "9"))
    ) nameEnd++;
    const name = lower.slice(k, nameEnd);

    i = tagEnd;

    // Script and style bodies are never content: drop them with the tag.
    if (!closing && (name === "script" || name === "style")) {
      const closer = `</${name}`;
      const at = noCloser[name] ? -1 : lower.indexOf(closer, tagEnd);
      if (at === -1) {
        noCloser[name] = true;
      } else {
        const closeEnd = text.indexOf(">", at);
        i = closeEnd === -1 ? n : closeEnd + 1;
      }
      continue;
    }

    if (name === "br") {
      push("\n");
    } else if (name === "li" && !closing) {
      ensureNewline();
      push("- ");
    } else if (closing && BLOCK_TAGS.has(name)) {
      push("\n");
    }
  }

  // Tidy whitespace: trim line ends, collapse runs of blank lines, trim edges.
  return out
    .join("")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
